<div class="content-info">

  <!-- Page Header -->
  <div class="content-title">
    <h3 class="title">Vendor SKU Mapping</h3>
    @if (unmappedCount > 0) {
    <div class="alert alert-warning d-inline-flex align-items-center gap-2 py-1 px-3 mb-0" role="alert">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <span>{{ unmappedCount }} product{{ unmappedCount !== 1 ? 's have' : ' has' }} no vendor assigned</span>
    </div>
    }
  </div>

  <!-- Vendor Selector -->
  <div class="product-catg">
    <div class="row">
      <div class="col-12 col-md-4">
        <label class="form-label fw-semibold">Select Vendor</label>
        <ng-select
          [items]="vendors"
          bindLabel="name"
          placeholder="Select a vendor..."
          [searchable]="true"
          [clearable]="true"
          [(ngModel)]="selectedVendor"
          (change)="onVendorChange()"
          [attr.aria-label]="'Select vendor'">
        </ng-select>
      </div>
    </div>
  </div>

  <!-- No vendor selected — placeholder -->
  @if (!selectedVendor) {
  <div class="card border-0 shadow-sm">
    <div class="card-body text-center py-5 text-muted">
      <i class="bi bi-box-seam fs-1 mb-3 d-block"></i>
      <h6 class="mb-1">Select a vendor to get started</h6>
      <p class="mb-3 small">Choose a vendor above to view and manage its product mappings.</p>
      <button class="btn btn-outline-secondary btn-sm" (click)="downloadTemplate()">
        <i class="bi bi-download me-1"></i>Download Template
      </button>
    </div>
  </div>
  }

  @if (selectedVendor) {

  <!-- Manual Add Card -->
  <div class="card mb-3">
    <div class="card-header">
      <span class="fw-semibold">Add SKU to {{ selectedVendor.name }}</span>
    </div>
    <div class="card-body">
      <div class="row align-items-start g-2">
        <div class="col-12 col-md-6">
          <app-search-select
            entity="product"
            bindLabel="name"
            [items]="productOptions"
            [typeahead]="productSearch$"
            [loading]="isSearchingProducts"
            [clearable]="true"
            placeholder="Search by SKU or name to map..."
            [(ngModel)]="selectedProduct">
            <ng-template #optionTpl let-item="item">
              <div class="ss-row">
                <div class="ss-row__primary" [title]="item.name">
                  {{ item.name }}
                  @if (item.alreadyMapped) {
                  <span class="badge text-bg-light border ms-1">Already mapped</span>
                  }
                </div>
                <div class="ss-row__secondary">SKU {{ item.sku }}</div>
              </div>
            </ng-template>
          </app-search-select>
          @if (selectedProduct?.alreadyMapped) {
          <div class="text-muted small mt-1">
            <i class="bi bi-info-circle me-1"></i>This SKU is already mapped to this vendor.
          </div>
          }
        </div>
        <div class="col-6 col-md-2">
          <label for="manualMinQty" class="form-label visually-hidden">Min Qty</label>
          <input
            id="manualMinQty"
            type="number"
            min="1"
            step="1"
            class="form-control"
            placeholder="Min Qty"
            [(ngModel)]="manualMinOrderQty"
            [attr.aria-label]="'Minimum order quantity'" />
        </div>
        <div class="col-6 col-md-2">
          <label for="manualLeadDays" class="form-label visually-hidden">Lead Days</label>
          <input
            id="manualLeadDays"
            type="number"
            min="0"
            step="1"
            class="form-control"
            placeholder="Lead Days"
            [(ngModel)]="manualLeadTime"
            [attr.aria-label]="'Lead time in days'" />
        </div>
        <div class="col-auto">
          <button
            class="btn btnprimary"
            [disabled]="!selectedProduct || selectedProduct.alreadyMapped || isAddingManual"
            (click)="addManualMapping()"
            [attr.aria-busy]="isAddingManual">
            @if (isAddingManual) {
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Adding...
            } @else {
            <i class="bi bi-plus-lg me-1"></i>Add
            }
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Upload Card -->
  <div class="card mb-3">
    <div class="card-header d-flex justify-content-between align-items-center">
      <span class="fw-semibold">Upload SKUs for {{ selectedVendor.name }}</span>
      <button class="btn btn-outline-secondary btn-sm" (click)="downloadTemplate()">
        <i class="bi bi-download me-1"></i>Template
      </button>
    </div>
    <div class="card-body">
      <div class="row align-items-start g-2">
        <div class="col-12 col-md-6">
          <label for="skuFileInput" class="form-label visually-hidden">Excel file</label>
          <input
            id="skuFileInput"
            type="file"
            class="form-control"
            accept=".xlsx,.xls"
            (change)="onFileChange($event)"
            [attr.aria-label]="'Upload SKU Excel file'" />
          @if (fileError) {
          <div class="text-danger small mt-1">{{ fileError }}</div>
          }
        </div>
        <div class="col-auto">
          <button
            class="btn btnprimary"
            [disabled]="!selectedFile || isPreviewing"
            (click)="openPreview()"
            [attr.aria-busy]="isPreviewing">
            @if (isPreviewing) {
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Previewing...
            } @else {
            Preview <i class="bi bi-arrow-right ms-1"></i>
            }
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mapped SKUs Toolbar -->
  <div class="d-flex justify-content-between align-items-center mb-2">
    <div class="d-flex align-items-center gap-2 flex-wrap">
      <span class="fw-semibold">Mapped SKUs</span>
      <span class="badge bg-secondary">{{ total }}</span>
      <div class="input-group input-group-sm mapped-search">
        <span class="input-group-text bg-white border-end-0">
          <i class="bi bi-search"></i>
        </span>
        <input
          type="search"
          class="form-control border-start-0"
          placeholder="Search SKU or description..."
          [ngModel]="mappedSearch"
          (ngModelChange)="onMappedSearchChange($event)"
          [attr.aria-label]="'Search mapped SKUs'" />
        @if (mappedSearch) {
        <button
          class="btn btn-outline-secondary"
          type="button"
          (click)="clearMappedSearch()"
          aria-label="Clear search">
          <i class="bi bi-x-lg"></i>
        </button>
        }
      </div>
    </div>
    <div class="d-flex gap-2">
      <button
        class="btn btn-outline-secondary btn-sm"
        [disabled]="isExporting"
        (click)="exportMappings()">
        @if (isExporting) {
        <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        } @else {
        <i class="bi bi-file-earmark-excel me-1"></i>
        }
        Export Excel
      </button>
      @if (selectedProductIds.size > 0) {
      <button
        class="btn btn-danger btn-sm"
        [disabled]="isBulkRemoving"
        (click)="bulkRemove()"
        aria-live="polite">
        @if (isBulkRemoving) {
        <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        } @else {
        <i class="bi bi-trash me-1"></i>
        }
        Remove ({{ selectedProductIds.size }})
      </button>
      }
    </div>
  </div>

  <!-- Mapped SKUs Table -->
  <div class="common-table">
    <div class="table-responsive" style="max-height: 65vh; overflow-y: auto; overflow-x: auto;">
      <table class="table table-hover mb-0">
        <thead class="table-light sticky-top">
          <tr>
            <th scope="col" style="width: 40px;">
              <input
                #selectAllCheckbox
                type="checkbox"
                class="form-check-input"
                [checked]="allSelected"
                [indeterminate]="selectedProductIds.size > 0 && !allSelected"
                (change)="toggleAll(selectAllCheckbox.checked)"
                [disabled]="isLoadingMappings || mappings.length === 0" />
            </th>
            <th scope="col">SKU</th>
            <th scope="col" class="d-none d-md-table-cell">Description</th>
            <th scope="col" class="d-none d-md-table-cell">Min Qty</th>
            <th scope="col" class="d-none d-md-table-cell">Lead Days</th>
            <th scope="col" style="width: 60px;">Action</th>
          </tr>
        </thead>
        <tbody>
          @if (isLoadingMappings) {
          <!-- Skeleton rows -->
          @for (i of [1, 2, 3]; track i) {
          <tr>
            <td><span class="placeholder-glow"><span class="placeholder col-12"></span></span></td>
            <td><span class="placeholder-glow"><span class="placeholder col-8"></span></span></td>
            <td class="d-none d-md-table-cell"><span class="placeholder-glow"><span class="placeholder col-10"></span></span></td>
            <td class="d-none d-md-table-cell"><span class="placeholder-glow"><span class="placeholder col-4"></span></span></td>
            <td class="d-none d-md-table-cell"><span class="placeholder-glow"><span class="placeholder col-4"></span></span></td>
            <td><span class="placeholder-glow"><span class="placeholder col-12"></span></span></td>
          </tr>
          }
          } @else if (mappings.length === 0) {
          <!-- Empty state -->
          <tr>
            <td colspan="6" class="text-center py-5 text-muted">
              @if (mappedSearch) {
              <i class="bi bi-search fs-1 mb-3 d-block"></i>
              <h6 class="mb-1">No mapped SKUs match "{{ mappedSearch }}"</h6>
              <p class="mb-3 small">Try a different SKU or description.</p>
              <button class="btn btn-outline-secondary btn-sm" (click)="clearMappedSearch()">
                Clear search
              </button>
              } @else {
              <i class="bi bi-clipboard-x fs-1 mb-3 d-block"></i>
              <h6 class="mb-1">No SKUs mapped yet</h6>
              <p class="mb-3 small">Add a SKU above or upload an Excel file to assign products to this vendor.</p>
              <button class="btn btn-outline-secondary btn-sm" (click)="downloadTemplate()">
                <i class="bi bi-download me-1"></i>Download Template
              </button>
              }
            </td>
          </tr>
          } @else {
          @for (row of mappings; track row.productId) {
          <tr [class.table-active]="isRowSelected(row.productId)">
            <td>
              <input
                #rowCheckbox
                type="checkbox"
                class="form-check-input"
                [checked]="isRowSelected(row.productId)"
                (change)="toggleRow(row.productId, rowCheckbox.checked)"
                [attr.aria-label]="'Select ' + row.sku" />
            </td>
            <td class="fw-medium">
              {{ row.sku }}
              @if (row.isPrimary) {
              <span class="badge text-bg-light border ms-1" title="Primary vendor on the product master">Primary</span>
              }
            </td>
            <td class="d-none d-md-table-cell text-muted small">{{ row.name }}</td>
            <td class="d-none d-md-table-cell">{{ row.minOrderQty }}</td>
            <td class="d-none d-md-table-cell">{{ row.leadTime ?? '—' }}</td>
            <td>
              <button
                class="btn btn-sm btn-outline-danger"
                [disabled]="removingProductId === row.productId || row.isPrimary"
                [title]="row.isPrimary ? 'Primary vendor — change it on the product master to remove' : ''"
                (click)="removeMapping(row.productId)"
                [attr.aria-label]="'Remove ' + row.sku">
                @if (removingProductId === row.productId) {
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                } @else {
                <i class="bi bi-trash"></i>
                }
              </button>
            </td>
          </tr>
          }
          }
        </tbody>
      </table>
    </div>
  </div>

  <!-- Pagination -->
  @if (totalPages > 1) {
  <nav class="mt-3" aria-label="SKU mapping pagination">
    <ul class="pagination justify-content-center mb-0">
      <li class="page-item" [class.disabled]="page === 1">
        <button class="page-link" (click)="goToPage(page - 1)">&laquo; Prev</button>
      </li>
      @for (p of pageNumbers; track p) {
      <li class="page-item" [class.active]="p === page">
        <button class="page-link" (click)="goToPage(p)">{{ p }}</button>
      </li>
      }
      <li class="page-item" [class.disabled]="page === totalPages">
        <button class="page-link" (click)="goToPage(page + 1)">Next &raquo;</button>
      </li>
    </ul>
  </nav>
  }

  } <!-- end @if selectedVendor -->

</div>

<!-- Preview Modal -->
@if (showPreviewModal && previewResult) {
<div
  class="modal fade show d-block"
  tabindex="-1"
  role="dialog"
  aria-modal="true"
  aria-labelledby="previewModalTitle"
  style="background-color: rgba(0,0,0,0.5); z-index: 999999; position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content text-dark">

      <div class="modal-header">
        <h5 class="modal-title" id="previewModalTitle">
          Preview: Upload for {{ selectedVendor?.name }}
        </h5>
        <button type="button" class="btn-close" aria-label="Close" (click)="closePreviewModal()"></button>
      </div>

      <div class="modal-body">
        @if (!previewCanConfirm && previewResult.notFound.length > 0) {
        <div class="alert alert-warning mb-3">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          None of the SKUs in this file exist in the Product Master. Please create these SKUs in the
          Product Master first, then upload the file again.
        </div>
        }
        @if (previewResult.found > 0) {
        <p class="text-success mb-2">
          <i class="bi bi-check-circle-fill me-2"></i>
          <strong>{{ previewResult.found }}</strong> SKU{{ previewResult.found !== 1 ? 's' : '' }} will be mapped
        </p>
        }
        @if (previewResult.updated > 0) {
        <p class="text-primary mb-2">
          <i class="bi bi-arrow-repeat me-2"></i>
          <strong>{{ previewResult.updated }}</strong> SKU{{ previewResult.updated !== 1 ? 's' : '' }} will be updated (already mapped)
        </p>
        }
        @if (previewResult.notFound.length > 0) {
        <p class="text-danger mb-2">
          <i class="bi bi-x-circle-fill me-2"></i>
          <strong>{{ previewResult.notFound.length }}</strong> SKU{{ previewResult.notFound.length !== 1 ? 's' : '' }} not available in the Product Master
{{ previewCanConfirm ? ' — these rows will be skipped' : '' }}
        </p>
        <div class="border rounded bg-light px-3 py-2 small text-muted" style="max-height: 120px; overflow-y: auto;">
          {{ previewResult.notFound.join(', ') }}
        </div>
        <p class="small text-muted mt-2 mb-0">
          <i class="bi bi-info-circle me-1"></i>
          Add {{ previewResult.notFound.length !== 1 ? 'these SKUs' : 'this SKU' }} in the Product Master
          first, then upload the file again to map {{ previewResult.notFound.length !== 1 ? 'them' : 'it' }}
          to {{ selectedVendor?.name }}.
        </p>
        }
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btnsecondary" (click)="closePreviewModal()">Cancel</button>
        <button
          type="button"
          class="btn btnprimary"
          [disabled]="!previewCanConfirm || isUploading"
          (click)="confirmUpload()">
          @if (isUploading) {
          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Uploading...
          } @else {
          Confirm Upload <i class="bi bi-arrow-right ms-1"></i>
          }
        </button>
      </div>

    </div>
  </div>
</div>
}
